import { fetchClient } from "@/api/api";
import type {
	MarkAsFailedInput,
	MarkAsFailedOutput,
	MarkAsPaidInput,
	MarkAsPaidOutput,
	PaymentsFacetsInput,
	PaymentsFacetsOutput,
	RevertMultiplePaymentInput,
	RevertMultiplePaymentOutput,
	RevertSinglePaymentInput,
	RevertSinglePaymentOutput,
	assignPaymentOperatorToPaymentsInput,
	assignPaymentOperatorToPaymentsOutput,
	checkListedPlayersInput,
	checkListedPlayersOutput,
	createBatchPaymentInput,
	createBatchPaymentOutput,
	createPaymentInput,
	createPaymentOutput,
	getAdminPaymentsFacetsInput,
	getAdminPaymentsFacetsOutput,
	getAdminPaymentsInput,
	getAdminPaymentsOutput,
	getAllPaymentsInput,
	getAllPaymentsOutput,
	getPaymentInput,
	getPaymentOutput,
	getPoPaymentsFacetsInput,
	getPoPaymentsFacetsOutput,
	getPoPaymentsInput,
	getPoPaymentsOutput,
	revertPaymentStatusFromFailedToPendingInput,
	revertPaymentStatusFromFailedToPendingOutput,
	revertPaymentsStatusFromFailedToPendingInput,
	revertPaymentsStatusFromFailedToPendingOutput,
	updatePaymentByAdminInput,
	updatePaymentByAdminOutput,
	updatePaymentInput,
	updatePaymentOutput,
} from "@/api/custom_types";
import useApiError from "@/hooks/useApiError";
import { ADMIN_FC_USERS_SERVICE_KEYS, USERS_SERVICE_KEYS } from "@/services";
import { createFacetSource } from "@/services/facets";
import type { ApiError } from "@/utils/fetching";
import {
	convertAdminPaymentFacetsToFilterFormat,
	convertPaymentFacetsToFilterFormat,
} from "@/utils/filters";
import type { CustomMutationHook, CustomQueryHook } from "@/utils/react-query";
import { invalidateQueries } from "@/utils/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { PAYMENTS_SERVICE_KEYS } from "./keys";

export const useAssignPaymentOperatorToPayments: CustomMutationHook<
	assignPaymentOperatorToPaymentsInput,
	assignPaymentOperatorToPaymentsOutput
> = (options) => {
	const { toastApiError } = useApiError();

	return useMutation<
		assignPaymentOperatorToPaymentsOutput,
		ApiError,
		assignPaymentOperatorToPaymentsInput
	>({
		...options,
		mutationKey: PAYMENTS_SERVICE_KEYS.useAssignPaymentOperatorToPayments(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.POST(
				"/v1/fc/payments/operator",
				{
					body,
					throwOnError: true,
				},
			);
			if (error) {
				throw error;
			}
			return data;
		},
		onSuccess: (...args) => {
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetAdminPayments());
			options?.onSuccess?.(...args);
		},
		onError: (error, variables, onMutateResult, mutationContext) => {
			if (!options?.onError) {
				toastApiError(error);
			}
			options?.onError?.(error, variables, onMutateResult, mutationContext);
		},
	});
};

export const useRevertPaymentStatusFromFailedToPending: CustomMutationHook<
	revertPaymentStatusFromFailedToPendingInput,
	revertPaymentStatusFromFailedToPendingOutput
> = (options) => {
	const { toastApiError } = useApiError();

	return useMutation<
		revertPaymentStatusFromFailedToPendingOutput,
		ApiError,
		revertPaymentStatusFromFailedToPendingInput
	>({
		...options,
		mutationKey:
			PAYMENTS_SERVICE_KEYS.useRevertPaymentStatusFromFailedToPending(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/fc/admin/payments/revert-status/{payment_id}",
				{
					params: { path: payload },
					throwOnError: true,
				},
			);
			if (error) {
				throw error;
			}
			return data;
		},
		onSuccess: (...args) => {
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetAllPayments());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetAdminPayments());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetAdminPaymentsFacets());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetPoPayments());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetPoPaymentsFacets());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetPaymentsFacets());
			options?.onSuccess?.(...args);
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useRevertPaymentsStatusFromFailedToPending: CustomMutationHook<
	revertPaymentsStatusFromFailedToPendingInput,
	revertPaymentsStatusFromFailedToPendingOutput
> = (options) => {
	const { toastApiError } = useApiError();

	return useMutation<
		revertPaymentsStatusFromFailedToPendingOutput,
		ApiError,
		revertPaymentsStatusFromFailedToPendingInput
	>({
		...options,
		mutationKey:
			PAYMENTS_SERVICE_KEYS.useRevertPaymentsStatusFromFailedToPending(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/fc/admin/payments/revert-status/",
				{
					body: payload,
					throwOnError: true,
				},
			);
			if (error) {
				throw error;
			}
			return data;
		},
		onSuccess: (...args) => {
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetAllPayments());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetAdminPayments());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetAdminPaymentsFacets());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetPoPayments());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetPoPaymentsFacets());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetPaymentsFacets());
			options?.onSuccess?.(...args);
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useGetAllPayments: CustomQueryHook<
	getAllPaymentsOutput,
	NonNullable<getAllPaymentsInput>
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: PAYMENTS_SERVICE_KEYS.useGetAllPayments(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/v1/fc/payments/", {
				params: { query: payload },
				throwOnError: true,
			});

			if (error) {
				throw error;
			}

			return data;
		},
	});
};

export const useGetPoPayments: CustomQueryHook<
	getPoPaymentsOutput,
	NonNullable<getPoPaymentsInput>
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: PAYMENTS_SERVICE_KEYS.useGetPoPayments(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/v1/fc/payments/po/", {
				params: { query: payload },
				throwOnError: true,
			});

			if (error) {
				throw error;
			}

			return data;
		},
	});
};

export const useGetAdminPayments: CustomQueryHook<
	getAdminPaymentsOutput,
	NonNullable<getAdminPaymentsInput>
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: PAYMENTS_SERVICE_KEYS.useGetAdminPayments(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/v1/fc/payments/admin/", {
				params: { query: payload },
				throwOnError: true,
			});

			if (error) {
				throw error;
			}

			return data;
		},
	});
};

export const useCreatePayment: CustomMutationHook<
	createPaymentInput,
	createPaymentOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<createPaymentOutput, ApiError, createPaymentInput>({
		mutationKey: PAYMENTS_SERVICE_KEYS.useCreatePayment(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.POST("/v1/fc/payments/", {
				body,
				throwOnError: true,
			});
			if (error) {
				throw error;
			}
			return data;
		},

		onSuccess: () => {
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetAllPayments());
			invalidateQueries(USERS_SERVICE_KEYS.useGetUserWallet());
			invalidateQueries(USERS_SERVICE_KEYS.useGetUser());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetPaymentsFacets());
			invalidateQueries(ADMIN_FC_USERS_SERVICE_KEYS.useGetAdminFcUsers());
		},

		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useCreateBatchPayment: CustomMutationHook<
	createBatchPaymentInput,
	createBatchPaymentOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<
		createBatchPaymentOutput,
		ApiError,
		createBatchPaymentInput
	>({
		mutationKey: PAYMENTS_SERVICE_KEYS.useCreateBatchPayment(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.POST("/v1/fc/payments/batch/", {
				body,
				throwOnError: true,
			});
			if (error) {
				throw error;
			}
			return data;
		},

		onSuccess: () => {
			invalidateQueries(ADMIN_FC_USERS_SERVICE_KEYS.useGetAdminFcUsers());
		},

		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useGetPayment: CustomQueryHook<getPaymentOutput, getPaymentInput> =
	(options, queryOptions) => {
		return useQuery({
			...queryOptions,
			queryKey: PAYMENTS_SERVICE_KEYS.useGetPayment(options),
			enabled:
				queryOptions?.enabled !== undefined
					? queryOptions.enabled && !!options.payment_id
					: !!options.payment_id,
			queryFn: async () => {
				const { data, error } = await fetchClient.GET(
					"/v1/fc/payments/{payment_id}",
					{
						params: { path: { payment_id: options.payment_id } },
						throwOnError: true,
					},
				);

				if (error) throw error;
				return data;
			},
		});
	};

export const useUpdatePayment: CustomMutationHook<
	updatePaymentInput,
	updatePaymentOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<updatePaymentOutput, ApiError, updatePaymentInput>({
		mutationKey: PAYMENTS_SERVICE_KEYS.useUpdatePayment(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.PATCH(
				"/v1/fc/payments/{payment_id}",
				{
					params: { path: { payment_id: payload.payment_id } },
					throwOnError: true,
				},
			);
			if (error) {
				throw error;
			}
			return data;
		},
		onSuccess: (_, variables) => {
			invalidateQueries(
				PAYMENTS_SERVICE_KEYS.useGetPayment({
					payment_id: variables.payment_id,
				}),
			);
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetPoPayments());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetAdminPayments());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetAllPayments());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetPaymentsFacets());
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useUpdatePaymentByAdmin: CustomMutationHook<
	updatePaymentByAdminInput,
	updatePaymentByAdminOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<
		updatePaymentByAdminOutput,
		ApiError,
		updatePaymentByAdminInput
	>({
		mutationKey: [...PAYMENTS_SERVICE_KEYS.useUpdatePayment(), "admin"],
		mutationFn: async (payload) => {
			const { payment_id, ...body } = payload;
			const { data, error } = await fetchClient.PATCH(
				"/v1/fc/payments/{payment_id}/admin",
				{
					params: { path: { payment_id } },
					body,
					throwOnError: true,
				},
			);
			if (error) {
				throw error;
			}
			return data;
		},
		onSuccess: (_, variables) => {
			invalidateQueries(
				PAYMENTS_SERVICE_KEYS.useGetPayment({
					payment_id: variables.payment_id,
				}),
			);
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetAdminPayments());
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useCheckListedPlayers: CustomQueryHook<
	checkListedPlayersOutput,
	NonNullable<checkListedPlayersInput>
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: PAYMENTS_SERVICE_KEYS.useCheckListedPlayers(payload),
		enabled: !!payload.payment_id,
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/payments/{payment_id}/listed-players/check/",
				{
					params: { path: { payment_id: payload.payment_id } },
					throwOnError: true,
				},
			);

			if (error) throw error;
			return data;
		},
	});
};

export const useGetPoPaymentsFacets: CustomQueryHook<
	getPoPaymentsFacetsOutput,
	NonNullable<getPoPaymentsFacetsInput>
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: PAYMENTS_SERVICE_KEYS.useGetPoPaymentsFacets(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/payments/po/facets/",
				{
					params: { query: payload },
					throwOnError: true,
				},
			);

			if (error) {
				throw error;
			}

			return data;
		},
	});
};

export const useGetAdminPaymentsFacets: CustomQueryHook<
	getAdminPaymentsFacetsOutput,
	NonNullable<getAdminPaymentsFacetsInput>
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: PAYMENTS_SERVICE_KEYS.useGetAdminPaymentsFacets(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/payments/admin/facets/",
				{
					params: { query: payload },
					throwOnError: true,
				},
			);

			if (error) {
				throw error;
			}

			return data;
		},
	});
};

export const usePaymentMarkAsFailed: CustomMutationHook<
	MarkAsFailedInput,
	MarkAsFailedOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<MarkAsFailedOutput, ApiError, MarkAsFailedInput>({
		mutationKey: PAYMENTS_SERVICE_KEYS.usePaymentMarkAsFailed(),
		mutationFn: async ({ path, body }) => {
			const { data, error } = await fetchClient.POST(
				"/v1/fc/payments/{payment_id}/fail",
				{
					params: { path },
					body,
					throwOnError: true,
				},
			);
			if (error) {
				throw error;
			}
			return data;
		},
		onSuccess: () => {
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetAllPayments());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetAdminPayments());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetAdminPaymentsFacets());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetPoPayments());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetPoPaymentsFacets());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetPaymentsFacets());
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const usePaymentMarkAsPaid: CustomMutationHook<
	MarkAsPaidInput,
	MarkAsPaidOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<MarkAsPaidOutput, ApiError, MarkAsPaidInput>({
		mutationKey: PAYMENTS_SERVICE_KEYS.usePaymentMarkAsPaid(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/fc/payments/{payment_id}/pay",
				{
					params: { path: payload },
					throwOnError: true,
				},
			);
			if (error) {
				throw error;
			}
			return data;
		},
		onSuccess: () => {
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetAllPayments());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetAdminPayments());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetAdminPaymentsFacets());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetPoPayments());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetPoPaymentsFacets());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetPaymentsFacets());
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useGetPaymentsFacets: CustomQueryHook<
	PaymentsFacetsOutput,
	NonNullable<PaymentsFacetsInput>
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: PAYMENTS_SERVICE_KEYS.useGetPaymentsFacets(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/v1/fc/payments/facets/", {
				params: { query: payload },
				throwOnError: true,
			});

			if (error) {
				throw error;
			}

			return data;
		},
	});
};

export const useRevertSinglePayment: CustomMutationHook<
	RevertSinglePaymentInput,
	RevertSinglePaymentOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<
		RevertSinglePaymentOutput,
		ApiError,
		RevertSinglePaymentInput
	>({
		mutationKey: PAYMENTS_SERVICE_KEYS.useRevertSinglePayment(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/fc/payments/{payment_id}/revert",
				{
					params: { path: payload },
					throwOnError: true,
				},
			);
			if (error) {
				throw error;
			}
			return data;
		},
		onSuccess: () => {
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetAllPayments());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetAdminPayments());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetAdminPaymentsFacets());
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useRevertMultiplePayments: CustomMutationHook<
	RevertMultiplePaymentInput,
	RevertMultiplePaymentOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<
		RevertMultiplePaymentOutput,
		ApiError,
		RevertMultiplePaymentInput
	>({
		mutationKey: PAYMENTS_SERVICE_KEYS.useRevertMultiplePayments(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST("/v1/fc/payments/revert", {
				body: payload,
				throwOnError: true,
			});
			if (error) {
				throw error;
			}
			return data;
		},
		onSuccess: () => {
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetAllPayments());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetAdminPayments());
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetAdminPaymentsFacets());
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

/**
 * Payment facets return `{ with_filter, without_filter }` per value in one
 * response. `useFacets` issues its own unfiltered and leave-one-out requests,
 * so it always reads `with_filter` — on the scope-only request that is the
 * scoped total, which `without_filter` cannot give (it ignores `user_id`).
 * The extra key segment keeps this reshaped cache entry apart from the raw one.
 */
export const ADMIN_PAYMENTS_FACET_SOURCE = createFacetSource<
	ReturnType<typeof convertAdminPaymentFacetsToFilterFormat>,
	getAdminPaymentsFacetsOutput
>({
	path: "/v1/fc/payments/admin/facets/",
	queryKey: (query) => [
		...PAYMENTS_SERVICE_KEYS.useGetAdminPaymentsFacets(query as any),
		"with_filter",
	],
	select: (raw) => convertAdminPaymentFacetsToFilterFormat(raw, true),
});

export const PO_PAYMENTS_FACET_SOURCE = createFacetSource<
	ReturnType<typeof convertPaymentFacetsToFilterFormat>,
	getPoPaymentsFacetsOutput
>({
	path: "/v1/fc/payments/po/facets/",
	queryKey: (query) => [
		...PAYMENTS_SERVICE_KEYS.useGetPoPaymentsFacets(query as any),
		"with_filter",
	],
	select: (raw) => convertPaymentFacetsToFilterFormat(raw, true),
});
